import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from 'react';

export type ChatInputSeedFn = (
	text: string,
	opts?: { flash?: boolean },
) => void;

type UserInputStartFn = () => void;

interface ChatInputSeedContextValue {
	seedChatInput: ChatInputSeedFn;
	registerHandler: (handler: ChatInputSeedFn | null) => void;
	notifyUserInputStart: UserInputStartFn;
	registerUserInputStartHandler: (handler: UserInputStartFn | null) => void;
}

const ChatInputSeedContext = createContext<ChatInputSeedContextValue | null>(
	null,
);

export const ChatInputSeedProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const handlerRef = useRef<ChatInputSeedFn | null>(null);
	const userInputStartRef = useRef<UserInputStartFn | null>(null);

	const value = useMemo<ChatInputSeedContextValue>(
		() => ({
			seedChatInput: (text, opts) => {
				handlerRef.current?.(text, opts);
			},
			registerHandler: (handler) => {
				handlerRef.current = handler;
			},
			notifyUserInputStart: () => {
				userInputStartRef.current?.();
			},
			registerUserInputStartHandler: (handler) => {
				userInputStartRef.current = handler;
			},
		}),
		[],
	);

	return (
		<ChatInputSeedContext.Provider value={value}>
			{children}
		</ChatInputSeedContext.Provider>
	);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useChatInputSeed = () => {
	const ctx = useContext(ChatInputSeedContext);
	if (!ctx) {
		throw new Error(
			'useChatInputSeed must be used inside ChatInputSeedProvider',
		);
	}
	return { seedChatInput: ctx.seedChatInput };
};

/**
 * Subscribe to chat-input seed events. The latest `handler` is always invoked,
 * so callers don't need to memoize it.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useRegisterChatInputSeedHandler = (handler: ChatInputSeedFn) => {
	const ctx = useContext(ChatInputSeedContext);
	const handlerRef = useRef(handler);
	handlerRef.current = handler;

	useEffect(() => {
		if (!ctx) return;
		const stable: ChatInputSeedFn = (text, opts) => {
			handlerRef.current(text, opts);
		};
		ctx.registerHandler(stable);
		return () => ctx.registerHandler(null);
	}, [ctx]);
};

// eslint-disable-next-line react-refresh/only-export-components
export const useNotifyUserInputStart = (): UserInputStartFn => {
	const ctx = useContext(ChatInputSeedContext);
	return ctx ? ctx.notifyUserInputStart : () => {};
};

// eslint-disable-next-line react-refresh/only-export-components
export const useRegisterUserInputStartHandler = (handler: UserInputStartFn) => {
	const ctx = useContext(ChatInputSeedContext);
	const handlerRef = useRef(handler);
	handlerRef.current = handler;

	useEffect(() => {
		if (!ctx) return;
		const stable: UserInputStartFn = () => {
			handlerRef.current();
		};
		ctx.registerUserInputStartHandler(stable);
		return () => ctx.registerUserInputStartHandler(null);
	}, [ctx]);
};
